import { useQuery, UseQueryResult } from '@tanstack/react-query'

import { paymentService } from '@/core/api/services/paymentService'

export interface AlegraLocationOption {
	id: string | number
	value: string
}

// CHL hooks
export const useAlegraRegions = (
	options?: any
): UseQueryResult<AlegraLocationOption[], Error> => {
	return useQuery({
		queryKey: ['alegra-regions'],
		queryFn: () =>
			paymentService.getAlegraRegions().then(({ data }) => {
				// Transform response to match expected format
				return Array.isArray(data?.data)
					? data.data.map((item: any, index: number) => ({
							id: item.id || item.value || `region-${index}`,
							value: item.name || item.label || item.value || item.id,
						}))
					: []
			}),
		cacheTime: 86400000, // 24h
		staleTime: 86400000, // 24h
		...options,
	})
}

export const useAlegraCommunes = (
	regionId?: string | number,
	options?: any
): UseQueryResult<AlegraLocationOption[], Error> => {
	return useQuery({
		queryKey: ['alegra-communes', regionId],
		queryFn: () =>
			paymentService
				.getAlegraCommunes({
					params: regionId ? { region_id: regionId } : {},
				})
				.then(({ data }) => {
					return Array.isArray(data?.data)
						? data.data.map((item: any, index: number) => ({
								id:
									item.id ||
									`${regionId}-${item.value || index}` ||
									`commune-${index}`,
								value: item.name || item.label || item.value || item.id,
							}))
						: []
				}),
		enabled: !!regionId,
		cacheTime: 86400000, // 24h
		staleTime: 86400000, // 24h
		...options,
	})
}

// ARG hooks
export const useAlegraProvinces = (
	options?: any
): UseQueryResult<AlegraLocationOption[], Error> => {
	return useQuery({
		queryKey: ['alegra-provinces'],
		queryFn: () =>
			paymentService.getAlegraProvinces().then(({ data }) => {
				return Array.isArray(data?.data)
					? data.data.map((item: any, index: number) => ({
							id: item.id || item.value || `province-${index}`,
							value: item.name || item.label || item.value || item.id,
						}))
					: []
			}),
		cacheTime: 86400000, // 24h
		staleTime: 86400000, // 24h
		...options,
	})
}

export const useAlegraCities = (
	provinceId?: string | number,
	options?: any
): UseQueryResult<AlegraLocationOption[], Error> => {
	return useQuery({
		queryKey: ['alegra-cities', provinceId],
		queryFn: () =>
			paymentService
				.getAlegraCities({
					params: provinceId ? { idProvince: provinceId } : {},
				})
				.then(({ data }) => {
					return Array.isArray(data?.data)
						? data.data.map((item: any, index: number) => ({
								id:
									item.id ||
									`${provinceId}-${item.value || index}` ||
									`city-${index}`,
								value: item.name || item.label || item.value || item.id,
							}))
						: []
				}),
		enabled: !!provinceId,
		cacheTime: 86400000, // 24h
		staleTime: 86400000, // 24h
		...options,
	})
}

// ESP hooks
export const useAlegraEsProvinces = (
	options?: any
): UseQueryResult<AlegraLocationOption[], Error> => {
	return useQuery({
		queryKey: ['alegra-es-provinces'],
		queryFn: () =>
			paymentService.getAlegraEsProvinces().then(({ data }) => {
				return Array.isArray(data?.data)
					? data.data.map((item: any, index: number) => ({
							id: item.id || item.value || `es-province-${index}`,
							value: item.name || item.label || item.value || item.id,
						}))
					: []
			}),
		cacheTime: 86400000, // 24h
		staleTime: 86400000, // 24h
		...options,
	})
}

export const useAlegraEsCities = (
	provinceId?: string | number,
	options?: any
): UseQueryResult<AlegraLocationOption[], Error> => {
	return useQuery({
		queryKey: ['alegra-es-cities', provinceId],
		queryFn: () =>
			paymentService
				.getAlegraEsCities({
					params: provinceId ? { idProvince: provinceId } : {},
				})
				.then(({ data }) => {
					return Array.isArray(data?.data)
						? data.data.map((item: any, index: number) => ({
								id:
									item.id ||
									`${provinceId}-${item.value || index}` ||
									`es-city-${index}`,
								value: item.name || item.label || item.value || item.id,
							}))
						: []
				}),
		enabled: !!provinceId,
		cacheTime: 86400000, // 24h
		staleTime: 86400000, // 24h
		...options,
	})
}

// COL hooks
export const useAlegraCoDepartments = (
	options?: any
): UseQueryResult<AlegraLocationOption[], Error> => {
	return useQuery({
		queryKey: ['alegra-co-departments'],
		queryFn: () =>
			paymentService.getAlegraCoDepartments().then(({ data }) => {
				return Array.isArray(data?.data)
					? data.data.map((item: any, index: number) => ({
							id: item.id || item.value || `co-department-${index}`,
							value: item.name || item.label || item.value || item.id,
						}))
					: []
			}),
		cacheTime: 86400000, // 24h
		staleTime: 86400000, // 24h
		...options,
	})
}

export const useAlegraCoCities = (
	departmentId?: string | number,
	options?: any
): UseQueryResult<AlegraLocationOption[], Error> => {
	return useQuery({
		queryKey: ['alegra-co-cities', departmentId],
		queryFn: () =>
			paymentService
				.getAlegraCoCities({
					params: departmentId ? { idDepartment: departmentId } : {},
				})
				.then(({ data }) => {
					return Array.isArray(data?.data)
						? data.data.map((item: any, index: number) => ({
								id:
									item.id ||
									`${departmentId}-${item.value || index}` ||
									`co-city-${index}`,
								value: item.name || item.label || item.value || item.id,
							}))
						: []
				}),
		enabled: !!departmentId,
		cacheTime: 86400000, // 24h
		staleTime: 86400000, // 24h
		...options,
	})
}

export interface AlegraCoZipCodeOption {
	cityId: string | number // The city ID this ZIP code belongs to
	value: string // The actual ZIP code value
}

/**
 * Hook to fetch all Colombia ZIP codes.
 * The backend returns all ZIP codes without filtering, so we fetch them once and cache.
 * Each ZIP code has: { id: cityId, value: zipCode }
 * Filtering by city should be done on the frontend using cityId.
 */
export const useAlegraCoZipCodes = (
	options?: any
): UseQueryResult<AlegraCoZipCodeOption[], Error> => {
	return useQuery({
		queryKey: ['alegra-co-zip-codes'],
		queryFn: () =>
			paymentService.getAlegraCoZipCodes({}).then(({ data }) => {
				return Array.isArray(data?.data)
					? data.data.map((item: any) => ({
							cityId: item.id, // The id field is the city ID
							value: item.value, // The value field is the ZIP code
						}))
					: []
			}),
		cacheTime: 86400000, // 24h
		staleTime: 86400000, // 24h
		...options,
	})
}
